import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as storage from '../utils/storage';
import getParams from '../utils/getParams';
import { createLiteDevice } from '../utils/createDevice';
import LogglyLogger from '../utils/LogglyLogger';
import init from '../core/init';
import SignUpQRCode from 'jsx:../qrcodes/signup.svg';

const apiUrl = process.env.API_URL;
if (!apiUrl) {
  throw new Error(`Missing environment variable 'API_URL'`);
}

const dashUrl = process.env.DASH_URL;
if (!dashUrl) {
  throw new Error(`Missing environment variable 'DASH_URL'`);
}

const pubnubPublishKey = process.env.PUBNUB_PUBLISH_KEY;
if (!pubnubPublishKey) {
  throw new Error(`Missing environment variable 'PUBNUB_PUBLISH_KEY'`);
}

const pubnubSubscribeKey = process.env.PUBNUB_SUBSCRIBE_KEY;
if (!pubnubSubscribeKey) {
  throw new Error(`Missing environment variable 'PUBNUB_SUBSCRIBE_KEY'`);
}

const logglyCustomerToken = process.env.LOGGLY_CUSTOMER_TOKEN;
if (!logglyCustomerToken) {
  throw new Error(`Missing environment variable 'LOGGLY_CUSTOMER_TOKEN'`);
}

const appRendererUrl = process.env.APP_RENDERER_URL;
const nodeEnv = process.env.NODE_ENV;
const revision = process.env.REVISION;
const environment = process.env.ENVIRONMENT || 'development';

const logger = new LogglyLogger({
  customerToken: logglyCustomerToken,
  enableFlushLogs: nodeEnv === 'production',
  enableConsoleLogs: nodeEnv !== 'production',
  tags: ['lite', environment],
});

const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const screenWidth = window.innerWidth;
const screenHeight = window.innerHeight;

// Session id is used to identify multiple lite instances using the same device id.
const sessionId = uuidv4();
logger.setGlobalContext({ sessionId });

if (appRendererUrl) {
  logger.info(`Overriding default app renderer url with '${appRendererUrl}'`);
}

const main = async () => {
  // Default to the latest version of lite core if version not provided.
  const {
    version = '0.7.3',
    debug = false,
    hostVersion,
    ...params
  } = getParams();

  // Get device id and access token from search params if provided,
  // otherwise get from storage. Search params should only be used
  // for debugging as it potentially exposes API keys in browser histories.
  let deviceId = params.deviceId ?? storage.getDeviceId();
  let accessToken = params.accessToken ?? storage.getAccessToken();

  logger.info(
    `Start session '${sessionId}', resolution=${screenWidth}x${screenHeight}`,
    {
      deviceId,
      userAgent,
      screenWidth,
      screenHeight,
      revision,
      hostVersion,
      liteCoreVersion: version,
    },
  );

  // Create the device if device id or api key is missing. This is
  // either the first load or local storage was cleared by the user.
  if (!deviceId || !accessToken) {
    ({ deviceId, accessToken } = await createLiteDevice());
    storage.setDeviceId(deviceId);
    storage.setAccessToken(accessToken);

    logger.info('New device created');
  }

  logger.mergeGlobalContext({ deviceId });

  // Asyncronously loads the renderer library. If debug is true, loads the unmifinied version.
  // Check which version of the device renderer to use.

  if (debug) {
    logger.info(`Loaded lite core version '${version}' in debug mode`);
  } else {
    logger.info(`Loaded lite core version '${version}'`);
  }

  // Render device.
  const container = document.getElementById('content');
  if (!container) throw new Error('Container not found.');

  await init({
    container,
    deviceId,
    sessionId,
    accessToken,
    apiUrl,
    dashUrl,
    pubnubPublishKey,
    pubnubSubscribeKey,
    pauseWhileOffline: true, // TODO: Use env var
    onDeviceDeleted: () => {
      logger.warn(`Device deleted, clearing storage and reloading.`);
      // Create a new device if this one gets deleted.
      localStorage.clear();
      // NOTE: Reloading here will cause the above log message to never be flushed.
      window.location.reload();
    },
    // Override the default app renderer url, should be set in development only.
    appRendererUrl,
    logger,
    injectAds: true,
    signUpPage: 'displai.ai/sign-up',
    signUpQrCode: React.createElement(SignUpQRCode),
  });

  setTimeout(() => {
    const splashEl = document.getElementById('splash');
    if (splashEl) {
      splashEl.remove();
    }
  }, 1000);
};

main().catch((err) => {
  const statusEl = document.getElementById('status');
  if (!statusEl) return;
  statusEl.innerHTML = err.message;

  logger.error(err);
});

window.addEventListener('error', (e) => {
  logger.error(e.error);
});

window.addEventListener('unhandledrejection', (e) => {
  logger.error(e.reason);
});
